'use client';

import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import type { Game, GameTrackingInfo } from '@/types/games';
import { useSnowplowUm } from '@/tracking/snowplow';
import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import {
    EVENT_TYPE,
    getGameElementDataFromZoneGame,
    SNOWPLOW_CONTEXT_MAPPING,
    type GameZoneSnowplowConfig
} from '@/tracking/snowplow/utils';
import { toSnakeCase } from '@/tracking/amplitude/utils';
import { useGlobalContext } from '@/store/contexts/global-context';

export type CategoryTrackingData = {
    categoryName: string;
    totalGamesInCategory: number;
    zoneUid: string;
    categoryUid: string;
};

const defaultValue: CategoryTrackingData = {
    categoryName: '',
    totalGamesInCategory: 0,
    zoneUid: '',
    categoryUid: ''
};

const CategoryTrackingContext = createContext<CategoryTrackingData>(defaultValue);

export const useCategoryTracking = () => useContext(CategoryTrackingContext);

type CategoryTrackingProviderProps = {
    value: CategoryTrackingData;
    children: ReactNode;
};

export const CategoryTrackingProvider = ({ value, children }: CategoryTrackingProviderProps) => {
    const normalized = useMemo(
        () => ({
            ...value,
            categoryName: toSnakeCase(value.categoryName)
        }),
        [value]
    );
    return <CategoryTrackingContext value={normalized}>{children}</CategoryTrackingContext>;
};

const MAX_GAME_CONTEXTS = 10;

/**
 * Fires a category_page_impression event (zone_interaction schema, action=impression)
 * once per distinct pathname. Attaches the first ≤10 games as game_element contexts.
 */
export const useCategoryPageImpressionEvent = (data: CategoryTrackingData, games: Game[]) => {
    const { sendEventToSnowplow } = useSnowplowUm();
    const pathname = usePathname();
    const previousPathnameRef = useRef<string | null>(null);
    const normalizedCategoryName = toSnakeCase(data.categoryName);

    useEffect(() => {
        const path = pathname ?? '';
        const prevPath = previousPathnameRef.current;
        previousPathnameRef.current = path;

        if (prevPath === path) return;

        void (async () => {
            try {
                await ensureCoreEventsInitialized();

                const categoryZoneConfig: GameZoneSnowplowConfig = {
                    zoneName: normalizedCategoryName,
                    zoneType: 'category_grid',
                    zonePosition: null,
                    zoneLayout: null
                };

                const gameDataContexts = games.slice(0, MAX_GAME_CONTEXTS).map((game, index) => ({
                    contextType: SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION,
                    contextProps: getGameElementDataFromZoneGame(
                        categoryZoneConfig,
                        index + 1,
                        true,
                        game as unknown as GameTrackingInfo
                    )
                }));

                sendEventToSnowplow({
                    event: EVENT_TYPE.CATEGORY_PAGE_IMPRESSION,
                    eventProps: {
                        action: 'impression',
                        zoneType: 'category_grid',
                        zoneName: normalizedCategoryName,
                        pageName: normalizedCategoryName,
                        zoneId: data.zoneUid || null,
                        zonePosition: null,
                        zoneLayout: null,
                        ctaLabel: null
                    },
                    contexts: gameDataContexts
                });
            } catch {
                // Logged in ensureCoreEventsInitialized.
            }
        })();
    }, [pathname, normalizedCategoryName, data.totalGamesInCategory, data.zoneUid, games, sendEventToSnowplow]);
};

/**
 * Fires the platform-appropriate lifecycle event once per distinct pathname:
 * - Android wrapper → category_screen_view (screen_view schema)
 * - Web → no-op (global PageViewTracker already fires trackPageView with full user context)
 */
export const useCategoryViewEvent = (data: CategoryTrackingData) => {
    const { sendEventToSnowplow } = useSnowplowUm();
    const { isAndroidWrapper } = useGlobalContext();
    const pathname = usePathname();
    const previousPathnameRef = useRef<string | null>(null);
    const normalizedCategoryName = toSnakeCase(data.categoryName);

    useEffect(() => {
        if (!isAndroidWrapper) return;

        const path = pathname ?? '';
        const prevPath = previousPathnameRef.current;
        previousPathnameRef.current = path;

        if (prevPath === path) return;

        void (async () => {
            try {
                await ensureCoreEventsInitialized();

                sendEventToSnowplow({
                    event: EVENT_TYPE.CATEGORY_SCREEN_VIEW,
                    eventProps: {
                        screenName: normalizedCategoryName
                    }
                });
            } catch {
                // Logged in ensureCoreEventsInitialized.
            }
        })();
    }, [pathname, normalizedCategoryName, isAndroidWrapper, sendEventToSnowplow]);
};
